/**
 * ProactivityLeaderboardTile — Feb 2026
 * =====================================
 *
 * Command Centre tile for HiBFF's weekly / monthly Proactive Champions
 * leaderboard. Renders the top 10 teens with a per-signal breakdown,
 * lets the founder pick the 1st / 2nd / 3rd winners + NFT counts, then
 * fires `POST /api/admin/proactivity/award` which mints the NFTs into
 * each winner's custodial vault + drops an in-app notification.
 *
 * Idempotent per (period_key, place) — the backend reports already-
 * awarded places so the "Award Now" button greys out for those slots
 * on re-visit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Trophy, Loader2, Sparkles, Award, Check, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const DEFAULT_TIER_COUNTS = { 1: 10, 2: 5, 3: 2 };
const TIER_LABEL = { 1: 'Gold', 2: 'Silver', 3: 'Bronze' };
const TIER_COLOR = {
  1: { bg: '#FEF3C7', text: '#92400E', accent: '#F59E0B' },  // gold
  2: { bg: '#F3F4F6', text: '#374151', accent: '#9CA3AF' },  // silver
  3: { bg: '#FEEBD7', text: '#7C2D12', accent: '#C2410C' },  // bronze
};

const SIGNAL_LABELS = {
  challenges_completed: 'Challenges',
  friends_invited:      'Invites',
  helped_others:        'Helped',
  rebecca_interactions: 'Rebecca',
  new_ideas:            'Ideas',
};

const ProactivityLeaderboardTile = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [awarding, setAwarding] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selections, setSelections] = useState({ 1: '', 2: '', 3: '' });
  const [tierCounts, setTierCounts] = useState({ ...DEFAULT_TIER_COUNTS });
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const d = await apiClient.request(`/api/admin/proactivity/leaderboard?period=${period}`);
      setData(d);
      // Pre-populate the podium picks from the ranked leaderboard so the
      // founder has a sensible default; they can override before clicking Award.
      const auto = {};
      (d?.top || []).slice(0, 3).forEach((row, idx) => { auto[idx + 1] = row.user_id; });
      setSelections({ 1: auto[1] || '', 2: auto[2] || '', 3: auto[3] || '' });
      setTierCounts({ ...DEFAULT_TIER_COUNTS });
    } catch (e) {
      setError(e?.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const alreadyAwarded = useMemo(
    () => new Set((data?.already_awarded_places || []).map(Number)),
    [data],
  );

  const remainingPlaces = useMemo(
    () => [1, 2, 3].filter(p => !alreadyAwarded.has(p)),
    [alreadyAwarded],
  );

  const totalNftsToAward = useMemo(
    () => remainingPlaces
      .filter(p => selections[p])
      .reduce((sum, p) => sum + (Number(tierCounts[p]) || 0), 0),
    [remainingPlaces, selections, tierCounts],
  );

  const handleAward = async () => {
    setAwarding(true); setError(''); setResult(null);
    try {
      const entries = remainingPlaces
        .filter(p => selections[p])
        .map(p => ({
          user_id: selections[p],
          place: p,
          nft_count: Number(tierCounts[p]) || DEFAULT_TIER_COUNTS[p],
        }));
      if (!entries.length) {
        setError('Pick at least one winner before awarding.');
        setAwarding(false);
        return;
      }
      const r = await apiClient.request('/api/admin/proactivity/award', {
        method: 'POST',
        body: JSON.stringify({ period, entries }),
      });
      setResult(r);
      // Refresh so already-awarded places lock out.
      load();
    } catch (e) {
      setError(e?.message || 'Award failed');
    } finally {
      setAwarding(false);
    }
  };

  const top = data?.top || [];

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-amber-100 p-5"
      data-testid="proactivity-leaderboard-tile"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: '#FEF3C7', color: '#92400E' }}>
            <Trophy size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Proactive Champions</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Top 10 teens this {period}. Award NFTs into their Vault after review.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-gray-100 p-1" data-testid="proactivity-period-toggle">
            {['week', 'month'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                  period === p ? 'bg-amber-500 text-white shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`proactivity-period-${p}`}
              >
                {p === 'week' ? 'Weekly' : 'Monthly'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            className="p-2 rounded-full hover:bg-gray-100"
            title="Refresh"
            data-testid="proactivity-refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="py-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      )}

      {!loading && error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2"
             data-testid="proactivity-error">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {!loading && !error && top.length === 0 && (
        <div className="py-10 text-center" data-testid="proactivity-empty">
          <Sparkles className="mx-auto text-gray-300 mb-2" size={22} />
          <p className="text-sm font-semibold text-gray-700">No proactive activity yet this {period}</p>
          <p className="text-xs text-gray-500 mt-1">Teens will appear once they complete challenges, invite friends, help others, chat with Rebecca or submit ideas.</p>
        </div>
      )}

      {!loading && !error && top.length > 0 && (
        <>
          {/* Ranked table */}
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm" data-testid="proactivity-table">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">#</th>
                  <th className="px-3 py-2 text-left font-semibold">Teen</th>
                  {Object.values(SIGNAL_LABELS).map((label, i) => (
                    <th key={i} className="px-2 py-2 text-right font-semibold hidden md:table-cell">{label}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-semibold">Score</th>
                </tr>
              </thead>
              <tbody>
                {top.map((row) => {
                  const podium = row.place <= 3;
                  const c = podium ? TIER_COLOR[row.place] : null;
                  return (
                    <tr
                      key={row.user_id}
                      className={`border-t border-gray-100 ${podium ? 'bg-amber-50/40' : ''}`}
                      data-testid={`proactivity-row-${row.place}`}
                    >
                      <td className="px-3 py-2 font-bold" style={c ? { color: c.accent } : { color: '#6B7280' }}>
                        {row.place}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-900">{row.name}</div>
                        <div className="text-[11px] text-gray-500 truncate max-w-[220px]">{row.email || row.user_id}</div>
                      </td>
                      {Object.keys(SIGNAL_LABELS).map((key) => (
                        <td key={key} className="px-2 py-2 text-right hidden md:table-cell text-gray-700 tabular-nums">
                          {row.counts?.[key] ?? 0}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-bold text-amber-700 tabular-nums">
                        {row.total_score}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Podium picker + Award button */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[1, 2, 3].map((place) => {
              const c = TIER_COLOR[place];
              const locked = alreadyAwarded.has(place);
              return (
                <div
                  key={place}
                  className="rounded-xl border-2 p-3"
                  style={{ borderColor: locked ? '#D1FAE5' : c.accent, background: locked ? '#F0FDF4' : c.bg }}
                  data-testid={`proactivity-podium-${place}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.text }}>
                      #{place} · {TIER_LABEL[place]}
                    </span>
                    {locked && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                        <Check size={11} /> Awarded
                      </span>
                    )}
                  </div>
                  <select
                    value={selections[place] || ''}
                    onChange={(e) => setSelections((s) => ({ ...s, [place]: e.target.value }))}
                    disabled={locked}
                    className="w-full text-sm rounded-lg border border-gray-200 bg-white px-2 py-1.5 mb-2 disabled:opacity-60"
                    data-testid={`proactivity-select-${place}`}
                  >
                    <option value="">— pick teen —</option>
                    {top.map((row) => (
                      <option key={row.user_id} value={row.user_id}>
                        #{row.place} · {row.name} ({row.total_score})
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 text-xs font-semibold" style={{ color: c.text }}>
                    NFT count
                    <input
                      type="number"
                      min={1}
                      max={25}
                      value={tierCounts[place]}
                      onChange={(e) => setTierCounts((t) => ({ ...t, [place]: Number(e.target.value) || 1 }))}
                      disabled={locked}
                      className="flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm tabular-nums disabled:opacity-60"
                      data-testid={`proactivity-count-${place}`}
                    />
                  </label>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-600">
              Ready to drop <strong className="text-amber-700">{totalNftsToAward}</strong> NFT{totalNftsToAward !== 1 ? 's' : ''}
              {' '}into <strong>{remainingPlaces.filter(p => selections[p]).length}</strong> teen Vault{remainingPlaces.filter(p => selections[p]).length !== 1 ? 's' : ''}.
              {' '}Period key: <code className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">{data?.period_key}</code>
            </div>
            <button
              type="button"
              onClick={handleAward}
              disabled={awarding || totalNftsToAward === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="proactivity-award-btn"
            >
              {awarding ? <Loader2 className="animate-spin" size={14} /> : <Award size={14} />}
              {awarding ? 'Minting…' : 'Award Now'}
            </button>
          </div>

          {result && (
            <div
              className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
              data-testid="proactivity-result"
            >
              <div className="font-bold flex items-center gap-2">
                <Check size={14} /> Awarded — {result.total_nfts_minted} NFT{result.total_nfts_minted !== 1 ? 's' : ''} dropped into {result.minted?.length || 0} Vault{result.minted?.length !== 1 ? 's' : ''}.
              </div>
              {result.skipped?.length > 0 && (
                <div className="mt-1 text-[11px] text-emerald-700">
                  Skipped {result.skipped.length}: {result.skipped.map(s => `${s.place}(${s.reason})`).join(', ')}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProactivityLeaderboardTile;
